import { useEffect, useMemo, useRef } from "react";
import { useAppStore } from "../../state/app-store";
import { useExternalDataStore } from "../../state/external-data-store";
import { joinPath } from "../../utils/class-utils";
import {
  buildLatestItemsSharedClasses,
  LatestItemsEditorNote,
  LatestItemsErrorState,
  LatestItemsLoadingState,
  LatestItemsSectionFrame,
} from "./shared/latest-items-ui";
import { TableInteractionNote } from "./shared/TableInteractionNote";
import { withReturnContext } from "./shared/link-context";
import { useEditorSafeAnchorClick } from "./shared/use-editor-safe-anchor-click";
import { CardItemIdRoot, RowItemIdRoot } from "./shared/item-id-comment";
import { formatEventTime } from "./shared/format-event-time";
import type { EventsRowProps, ResolvedEventItem } from "./types";

function buildIcsFilename(title: string, slug?: string): string {
  const base = (slug || title || "event")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);
  return `${base || "event"}.ics`;
}

function getEventTitle(evt: ResolvedEventItem): string {
  return evt.item_name ?? evt.page_title ?? evt.C_com_group_item_name ?? evt.slug ?? "Untitled";
}

export function EventsRow(props: EventsRowProps) {
  const {
    viewMode = "cards",
    basePath = "/events",
    bgClass = "no-class",
    paddingYClass = "py-section-md",
    cardBgClass = "no-class",
    cardBorderClass = "no-class",
    cardBorderColorClass = "no-class",
    contentAlignClass = "no-class",
    buttonColorClass = "primary",
    calendarButtonColorClass = "primary-100",
    itemLimit = "3",
  } = props;

  // Generate stable instance ID for this component
  const instanceIdRef = useRef<string>(`events-latest-${Math.random().toString(36).slice(2, 11)}`);
  const instanceId = instanceIdRef.current;

  const eventsLatestState = useExternalDataStore((state) => state.eventsLatest[instanceId]);
  const ensureEventsLatest = useExternalDataStore((state) => state.ensureEventsLatest);
  const loadLatestEvents = useExternalDataStore((state) => state.loadLatestEvents);
  const resetEventsLatest = useExternalDataStore((state) => state.resetEventsLatest);
  const { isEditor, handleAnchorClick } = useEditorSafeAnchorClick();
  const pageId = useAppStore((state) => state.documentId);

  const items = (eventsLatestState?.items ?? []) as ResolvedEventItem[];
  const isLoading = eventsLatestState?.isLoading ?? true;
  const errorText = eventsLatestState?.error ?? null;

  const classes = {
    ...buildLatestItemsSharedClasses({
      sectionClassName: "section-events-latest",
      bgClass,
      paddingYClass,
      cardBgClass,
      cardBorderClass,
      cardBorderColorClass,
      contentAlignClass,
      buttonColorClass,
    }),
    meta: "small d-flex flex-column gap-1",
    metaItem: "d-block",
    cta: "mt-auto d-flex justify-content-between gap-3 flex-wrap",
    tableLink: "text-decoration-none",
  };

  const stableBasePath = useMemo(() => basePath, [basePath]);
  const calendarButtonClassName = ["btn", "btn-sm", `btn-${calendarButtonColorClass || "primary-100"}`]
    .filter(Boolean)
    .join(" ");
  const downloadIcs = (icsContent: string, title: string, slug?: string) => {
    if (isEditor) return;
    const trimmedIcs = (icsContent || "").trim();
    if (!trimmedIcs) return;
    const blob = new Blob([trimmedIcs], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = buildIcsFilename(title, slug);
    link.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    ensureEventsLatest(instanceId);
    const limit = itemLimit === "all" ? 999 : parseInt(itemLimit, 10);
    void loadLatestEvents(instanceId, limit);
    return () => resetEventsLatest(instanceId);
  }, [ensureEventsLatest, instanceId, itemLimit, loadLatestEvents, resetEventsLatest]);

  if (isLoading) {
    return <LatestItemsLoadingState classes={classes} message="Loading latest events…" />;
  }

  if (errorText) {
    return <LatestItemsErrorState classes={classes} errorText={errorText} />;
  }

  // In live view, hide the entire section if there's no data
  if (!items.length && !isEditor) {
    return null;
  }

  const renderNoDataNote = () => (
    <LatestItemsEditorNote
      classes={classes}
      message="No events available at this time. This section will not appear in the live view until event content is available."
    />
  );

  const renderCards = () => (
    <div className={classes.row}>
      {items.map((evt, idx) => {
        const title = getEventTitle(evt);
        const href = evt.slug ? withReturnContext(joinPath(stableBasePath, evt.slug), pageId) : undefined;
        const eventIcs = typeof evt.calendar_event_data_ics === "string" ? evt.calendar_event_data_ics.trim() : "";
        const hasIcs = eventIcs.length > 0;

        return (
          <div className={classes.col} key={evt.__id ?? idx}>
            <CardItemIdRoot itemId={evt.item_id} className={classes.card}>
              <div className={classes.cardBody}>
                <div className={classes.header}>
                  <h3 className={classes.title}>{title}</h3>
                  <div className={classes.meta}>
                    {evt.start_date ? (
                      <span className={classes.metaItem}>
                        Date: {evt.start_date}
                        {(() => {
                          const startTime = formatEventTime(evt.start_time);
                          return startTime ? ` ${startTime}` : "";
                        })()}
                        {evt.end_date && evt.end_date !== evt.start_date ? ` - ${evt.end_date}` : ""}
                      </span>
                    ) : null}
                    {evt.location ? (
                      <span className={classes.metaItem} dangerouslySetInnerHTML={{ __html: "Location: " + evt.location }} />
                    ) : null}
                  </div>
                </div>

                <div className={classes.cta}>
                  {href ? (
                    <a
                      className={classes.ctaLink}
                      href={href}
                      onClick={handleAnchorClick}
                    >
                      View Event
                    </a>
                  ) : (
                    <button className={classes.ctaDisabled} type="button" disabled>
                      Missing slug
                    </button>
                  )}
                  {hasIcs ? (
                    <button
                      className={calendarButtonClassName}
                      type="button"
                      onClick={() => downloadIcs(eventIcs, title, evt.slug)}
                      disabled={isEditor}
                    >
                      Add to Calendar
                    </button>
                  ) : null}
                </div>
              </div>
            </CardItemIdRoot>
          </div>
        );
      })}
    </div>
  );

  const renderTable = () => (
    <div className={classes.tableWrap}>
      <div className="table-scroll">
        <table className={classes.table}>
        <thead className="text-bg-white sticky-top shadow-sm">
          <tr>
            <th scope="col">Event</th>
            <th scope="col">Date</th>
            <th scope="col">Location</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        <tbody>
          {items.map((evt, idx) => {
            const title = getEventTitle(evt);
            const href = evt.slug ? withReturnContext(joinPath(stableBasePath, evt.slug), pageId) : undefined;
            const eventIcs = typeof evt.calendar_event_data_ics === "string" ? evt.calendar_event_data_ics.trim() : "";
            const hasIcs = eventIcs.length > 0;
            const startTime = formatEventTime(evt.start_time);
            const dateRange = evt.start_date
              ? `${evt.start_date}${startTime ? ` ${startTime}` : ""}${evt.end_date && evt.end_date !== evt.start_date ? ` - ${evt.end_date}` : ""}`
              : "";

            return (
              <RowItemIdRoot itemId={evt.item_id} key={evt.__id ?? idx}>
                <td>
                  <span className={classes.tableTitle}>{title}</span>
                </td>
                <td className="text-nowrap">
                  {dateRange || <span>—</span>}
                </td>
                <td>
                  {evt.location ? (
                    <span dangerouslySetInnerHTML={{ __html: evt.location }} />
                  ) : (
                    <span>—</span>
                  )}
                </td>
                <td className="text-nowrap">
                  <div className="d-inline-flex gap-2 align-items-center">
                    {href ? (
                      <a
                        className={classes.tableCta}
                        href={href}
                        onClick={handleAnchorClick}
                      >
                        View Event
                      </a>
                    ) : (
                      <button className={classes.tableCtaDisabled} type="button" disabled>
                        Missing slug
                      </button>
                    )}
                    {hasIcs ? (
                      <button
                        className={calendarButtonClassName}
                        type="button"
                        onClick={() => downloadIcs(eventIcs, title, evt.slug)}
                        disabled={isEditor}
                      >
                        Add to Calendar
                      </button>
                    ) : null}
                  </div>
                </td>
              </RowItemIdRoot>
            );
          })}
        </tbody>
        </table>
      </div>
      <TableInteractionNote />
    </div>
  );

  return (
    <LatestItemsSectionFrame classes={classes} title="Upcoming Events">
      {items.length === 0 ? renderNoDataNote() : viewMode === "table" ? renderTable() : renderCards()}
    </LatestItemsSectionFrame>
  );
}
